<template>
  <div class="cleaning-form">
    <validation-observer
      ref="formValidation"
    >
      <!-- Cleaning Location Section -->
      <validation-provider
        v-slot="{ errors }"
        :name="$t('field.address')"
        rules="required"
      >
        <b-form-group
          :label="$t('field.cleaningLocation')"
          :state="errors.length === 0 ? null : false"
          class="mb-2"
        >
          <div v-if="hasAddressOptions">
            <!-- <b-form-radio-group
              v-model="addressMode"
              :options="addressModeOptions"
              :state="errors.length === 0 ? null : false"
              class="mb-1"
            /> -->

            <n-single-select
              v-if="addressMode === 'rental'"
              v-model="formData.address"
              :options="rentalAddressOptions"
              :selection-label="'text'"
              :selection-key="'value'"
              :reduce="true"
              :clearable="false"
              :placeholder="$t('placeholder.selectLocation')"
            />

            <b-form-textarea
              v-else
              v-model="customAddress"
              :rows="3"
              :state="errors.length === 0 ? null : false"
              :placeholder="$t('placeholder.enterAddress')"
              class="mt-1"
            />
          </div>
          <b-form-textarea
            v-else
            v-model="customAddress"
            :rows="3"
            :state="errors.length === 0 ? null : false"
            :placeholder="$t('placeholder.enterAddress')"
          />
          <small class="text-danger">{{ errors[0] }}</small>
        </b-form-group>
      </validation-provider>

      <!-- Service Description -->
      <validation-provider
        v-slot="{ errors }"
        :name="$t('field.description')"
        rules=""
      >
        <b-form-group
          :label="$t('field.serviceDescription')"
          :state="errors.length === 0 ? null : false"
          class="mb-2"
        >
          <b-form-textarea
            v-model="formData.description"
            :rows="3"
            :state="errors.length === 0 ? null : false"
            :placeholder="$t('placeholder.enterDescription')"
            maxlength="300"
          />
          <div class="d-flex justify-content-between align-items-center mt-1">
            <small class="text-muted">{{ formData.description.length }}/300</small>
            <small class="text-danger">{{ errors[0] }}</small>
          </div>
        </b-form-group>
      </validation-provider>

      <!-- Service Price (from parent) -->
      <div
        v-if="servicePrice && servicePrice.total != null"
        class="mt-1 mb-2"
      >
        <div :style="{ paddingBottom: 'calc(0.438rem + 1px)' }">
          <span>{{ $t('field.servicePrice') }}</span>
        </div>
        <div v-if="!hasActiveSubscription" class="subscription-warning mb-2">
          <div class="d-flex align-items-center b-c" role="alert">
            <feather-icon icon="AlertTriangleIcon" size="20" class="text-danger" />
            <span class="text-danger font-weight-bold">{{ $t('general.noActiveSubscription') }}</span>
          </div>
        </div>
        <div v-else class="text-body font-weight-bold">
          {{ totalPrice }}
        </div>
      </div>

      <!-- Service Time Section -->
      <validation-provider
        v-slot="{ errors }"
        :name="$t('field.scheduleType')"
        rules="required"
      >
        <b-form-group
          :label="$t('field.serviceTime')"
          :state="errors.length === 0 ? null : false"
          class="mb-2"
        >
          <b-form-radio-group
            v-model="formData.scheduleType"
            :options="[
              { text: $t('field.immediately'), value: 1 },
              { text: $t('field.customTime'), value: 2 }
            ]"
            :state="errors.length === 0 ? null : false"
          />
          <n-date-time-picker
            v-if="formData.scheduleType === 2"
            v-model="formData.scheduleDate"
            :state="errors.length === 0 ? null : false"
            :disable-before-today="true"
            :disable-after-today="false"
            class="mt-2"
            style="width: 50%"
          />
          <small class="text-danger">{{ errors[0] }}</small>
        </b-form-group>
      </validation-provider>
    </validation-observer>
  </div>
</template>

<script>
import {
  BFormGroup,
  BFormRadioGroup,
  BFormTextarea,
} from 'bootstrap-vue'
import { ValidationProvider, ValidationObserver } from 'vee-validate'
import NDateTimePicker from '@/components/NDateTimePicker.vue'
import NSingleSelect from '@/components/NSingleSelect.vue'
import { formatNumber } from '@/@core/utils/utils'
import FeatherIcon from '@/@core/components/feather-icon/FeatherIcon.vue'

export default {
  name: 'CleaningForm',

  components: {
    BFormGroup,
    BFormRadioGroup,
    BFormTextarea,
    ValidationProvider,
    ValidationObserver,
    NDateTimePicker,
    NSingleSelect,
    FeatherIcon,
  },

  props: {
    value: {
      type: Object,
      default: () => ({}),
    },
    servicePrice: {
      type: Object,
      default: null,
    },
    fullAddressList: {
      type: Array,
      default: () => [],
    },
    allowClipboard: {
      type: Boolean,
      default: false
    },
    hasActiveSubscription: {
      type: Boolean,
      default: true
    }
  },

  data() {
    return {
      formatNumber,
      formData: {
        scheduleType: 1,
        scheduleDate: '',
        address: null,
        description: '',
      },
      addressMode: 'other',
      descriptionDebounceTimer: null,
    }
  },

  computed: {
    totalPrice() {
      const total = this.servicePrice?.total
      if (total == null) return ''
      let price = `$${formatNumber(total)}`
      const type = this.servicePrice?.subscriptionType
      let suffix = ''
      if (type === 1) {
        suffix = ` / ${this.$t('general.time')} (${this.$t('general.chargePerServiceCall')})`
      } else if (type === 2) {
        suffix = ` / ${this.$t('general.unit')} (${this.$t('general.monthlyCharge')})`
      } else if (type === 3) {
        suffix = ` / m² (${this.$t('general.monthlyCharge')})`
      }
      return suffix ? price + suffix : price
    },
    addressOptions() {
      const list = Array.isArray(this.fullAddressList) ? this.fullAddressList : []
      const options = list.map(item => ({
        text: this.getAddressLabel(item),
        value: item,
      }))
      if (options.length > 0) {
        options.push({
          text: this.$t('field.other'),
          value: {
            isCustom: true,
            typeKey: 'other',
            typeLabel: this.$t('field.other'),
            addressName: null,
            customAddress: '',
          },
        })
      }
      return options
    },
    rentalAddressOptions() {
      const list = Array.isArray(this.fullAddressList) ? this.fullAddressList : []
      return list.map(item => ({
        text: this.getAddressLabel(item),
        value: item,
      }))
    },
    hasAddressOptions() {
      return this.addressOptions.length > 0
    },
    addressModeOptions() {
      return [
        { text: this.$t('general.rentalAddress'), value: 'rental' },
        { text: this.$t('general.otherAddress'), value: 'other' },
      ]
    },
    customAddress: {
      get() {
        if (this.formData.address && (this.formData.address.isCustom || this.formData.address.typeKey === 'other')) {
          return this.formData.address.customAddress || ''
        }
        return ''
      },
      set(val) {
        this.formData.address = {
          isCustom: true,
          typeKey: 'other',
          typeLabel: this.$t('field.other'),
          addressName: null,
          customAddress: val,
          files: [],
        }
      },
    },
  },

  watch: {
    value: {
      handler(newValue) {
        if (newValue && JSON.stringify(newValue) !== JSON.stringify(this.formData)) {
          this.formData = {
            scheduleType: newValue.scheduleType ?? 1,
            scheduleDate: newValue.scheduleDate ?? '',
            address: newValue.address ?? null,
            description: newValue.description ?? '',
          }
          this.addressMode = (this.formData.address && (this.formData.address.isCustom || this.formData.address.typeKey === 'other'))
            ? 'other'
            : (this.hasAddressOptions ? 'rental' : 'other')
        }
      },
      immediate: true,
      deep: true,
    },
    formData: {
      handler(newValue, oldValue) {
        if (JSON.stringify(newValue) !== JSON.stringify(this.value)) {
          // Check if only description changed
          const onlyDescriptionChanged = newValue.description !== (oldValue?.description ?? '')
            && JSON.stringify({ ...newValue, description: '' }) === JSON.stringify({ ...(oldValue || {}), description: '' })
          
          if (onlyDescriptionChanged) {
            // Debounce description changes
            if (this.descriptionDebounceTimer) clearTimeout(this.descriptionDebounceTimer)
            this.descriptionDebounceTimer = setTimeout(() => {
              const sanitizedValue = { ...newValue }
              this.$emit('input', sanitizedValue)
            }, 400)
          } else {
            // Emit immediately for other changes
            const sanitizedValue = { ...newValue }
            this.$emit('input', sanitizedValue)
          }
        }
      },
      deep: true,
    },
    addressMode(val) {
      if (val === 'rental') {
        // if currently other/custom, clear to force selection from rental list
        if (this.formData.address && (this.formData.address.isCustom || this.formData.address.typeKey === 'other')) {
          this.formData.address = null
        }
      } else if (val === 'other') {
        // if currently rental selected, clear to force re-input custom address
        if (this.formData.address && !(this.formData.address.isCustom || this.formData.address.typeKey === 'other')) {
          this.formData.address = null
        }
      }
    },
  },

  methods: {
    getAddressLabel(item) {
      if (!item) return ''
      const locale = this.getLocale()
      const parts = []
      
      // Handle localized typeLabel - add to front with dash
      let typeLabel = ''
      if (item.typeLabel) {
        if (typeof item.typeLabel === 'object') {
          typeLabel = item.typeLabel[locale] 
            || item.typeLabel.en 
            || item.typeLabel.zh 
            || item.typeLabel.km 
            || item.typeLabel.vi 
            || ''
        } else {
          typeLabel = item.typeLabel
        }
        if (typeLabel) parts.push(`${typeLabel}-`)
      }
      
      // Handle localized addressName from translation object
      const addressName = item.translation?.addressName?.[locale] || 
        item.translation?.addressName?.en ||
        item.translation?.addressName?.zh ||
        item.translation?.addressName?.km ||
        item.translation?.addressName?.vi ||
        item.addressName ||
        ''
      
      if (addressName) parts.push(addressName)
      
      return parts.join('')
    },
    getLocale() {
      const [locale] = this.$i18n.locale.split('-')
      return locale || 'en'
    },
    async validate() {
      if (!this.hasActiveSubscription) {
        return false
      }
      if (this.$refs.formValidation) {
        return this.$refs.formValidation.validate()
      }
      return false
    },

    reset() {
      this.formData = {
        scheduleType: 1,
        scheduleDate: null,
        address: null,
        description: '',
      }
      if (this.$refs.formValidation) {
        this.$refs.formValidation.reset()
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.cleaning-form {
  .form-group:last-child {
    margin-bottom: 0;
  }

  .price-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 1rem;
  }

  .price-card {
    background-color: #f8f9fa;
    border-radius: 0.5rem;
    padding: 1rem;
    text-align: center;

    .price {
      font-size: 1.25rem;
      font-weight: 600;
      margin-bottom: 0.25rem;
    }

    .label {
      font-size: 0.875rem;
      color: #6c757d;
    }
  }

  .subscription-warning {
    .b-c {
      display: flex;
      align-items: center;
      gap: 0.5rem;
    }

    .alert {
      .text-danger {
        font-size: 0.95rem;
        font-weight: 600;
      }
    }
  }
}
</style>